import { Box, IconButton, Stack, useTheme } from "@mui/material";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GoalActionBlockedDrawer } from "web_myplann/Goals/Components/GoalActionBlockedDrawer";
import { GoalCreatedPinDrawer } from "web_myplann/Goals/Components/GoalCreatedPinDrawer";
import { GoalDetailComposition } from "web_myplann/Goals/Components/GoalDetailComposition";
import { GoalDetailHero } from "web_myplann/Goals/Components/GoalDetailHero";
import { GoalDetailLoanCalculator } from "web_myplann/Goals/Components/GoalDetailLoanCalculator";
import { GoalDetailRentPlan } from "web_myplann/Goals/Components/GoalDetailRentPlan";
import { GoalDetailSkeleton } from "web_myplann/Goals/Components/GoalDetailSkeleton";
import { GoalDetailWhatIf } from "web_myplann/Goals/Components/GoalDetailWhatIf";
import {
	GoalPinningSection,
	type WelcomePinTrigger,
} from "web_myplann/Goals/Components/GoalPinningSection";
import { useGoalDetail } from "web_myplann/Goals/Hooks/useGoalDetail";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

type WelcomePinLocationState = {
	welcomePin?: boolean;
	ownFunds?: number;
};

export const GoalDetailPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const routeParams = useParams<{ id: string }>();
	const goalId = getTypedParam(routeParams, "id", Number) ?? null;
	const location = useLocation();
	const navigate = useNavigate();

	const { goal, dto: goalDto, isLoading } = useGoalDetail(goalId);
	const [blockedOpen, setBlockedOpen] = useState(false);
	const [welcomePinOpen, setWelcomePinOpen] = useState(false);
	const [welcomePinOwnFunds, setWelcomePinOwnFunds] = useState(0);
	const [welcomePinTrigger, setWelcomePinTrigger] =
		useState<WelcomePinTrigger | null>(null);

	useEffect(() => {
		const state = location.state as WelcomePinLocationState | null;
		if (state?.welcomePin) {
			setWelcomePinOwnFunds(state.ownFunds ?? 0);
			setWelcomePinOpen(true);
			navigate(location.pathname, { replace: true, state: null });
		}
	}, [location.state, location.pathname, navigate]);

	const handleWelcomePinAssign = () => {
		setWelcomePinOpen(false);
		setWelcomePinTrigger({ hasOwnFunds: welcomePinOwnFunds > 0 });
	};

	return (
		<MyPlannLayout
			bgColor={theme.palette.surface.muted}
			disableContentPadding
			topGlow
			topBar={
				<NavigationHeader
					title={t(Resources.Goals.Detail.title)}
					bgColor={theme.palette.background.paper}
					defaultRoute={Routing.Goals.GoalsList.path()}
					endComponent={
						goal &&
						goalId !== null && (
							<IconButton
								variant="soft"
								onClick={() =>
									goal.completed
										? setBlockedOpen(true)
										: navigate(Routing.Goals.GoalFormEdit.path(goalId))
								}
							>
								<Pencil size={theme.iconSize.sm} />
							</IconButton>
						)
					}
				/>
			}
		>
			{isLoading && !goal && <GoalDetailSkeleton />}

			{goal && (
				<Stack>
					<GoalDetailHero goal={goal} />
					<GoalPinningSection
						goal={goal}
						goalDto={goalDto}
						welcomePinTrigger={welcomePinTrigger}
						onWelcomePinTriggerHandled={() => setWelcomePinTrigger(null)}
					/>
					<Box mt={1}>
						<GoalDetailLoanCalculator goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailRentPlan goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailWhatIf goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailComposition goal={goal} />
					</Box>
				</Stack>
			)}
			<GoalActionBlockedDrawer
				open={blockedOpen}
				onClose={() => setBlockedOpen(false)}
				onMessageAdvisor={() => setBlockedOpen(false)}
			/>
			<GoalCreatedPinDrawer
				open={welcomePinOpen}
				ownFunds={welcomePinOwnFunds}
				onClose={() => setWelcomePinOpen(false)}
				onAssign={handleWelcomePinAssign}
			/>
		</MyPlannLayout>
	);
};
